import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, Scale } from 'lucide-react';
interface Episode6NavScreenProps {
  onBack: () => void;
}
export function Episode6NavScreen({
  onBack
}: Episode6NavScreenProps) {
  const [hasWatched, setHasWatched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showFinale, setShowFinale] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [showFinText, setShowFinText] = useState(false);
  const [canDismiss, setCanDismiss] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const handlePlayFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;
    setIsPlaying(true);
    setShowFinale(false);
    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
      await video.play();
    } catch (err) {
      await video.play();
    }
  }, []);
  useEffect(() => {
    if (hasAutoPlayed) return;
    const timer = setTimeout(() => {
      setHasAutoPlayed(true);
      handlePlayFullscreen();
    }, 800);
    return () => clearTimeout(timer);
  }, [hasAutoPlayed, handlePlayFullscreen]);
  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      ;
      (document as any).webkitExitFullscreen();
    }
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      setIsPlaying(false);
      exitFullscreen();
      setTimeout(() => setShowFinale(true), 400);
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (video && video.ended) {
          setIsPlaying(false);
          setTimeout(() => setShowFinale(true), 400);
        }
      }
    };
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [exitFullscreen]);
  // Staggered finale screen: 1s silence, then "FIN DE TEMPORADA" fades in
  useEffect(() => {
    if (!showFinale) {
      setShowFinText(false);
      setCanDismiss(false);
      return;
    }
    const finTimer = setTimeout(() => setShowFinText(true), 1000);
    const dismissTimer = setTimeout(() => setCanDismiss(true), 3000);
    return () => {
      clearTimeout(finTimer);
      clearTimeout(dismissTimer);
    };
  }, [showFinale]);
  const dismissFinale = () => {
    if (!canDismiss) return;
    setShowFinale(false);
    setHasWatched(true);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="min-h-screen w-full bg-[var(--bg-secondary)] flex flex-col relative overflow-hidden" variants={containerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-261">
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-overlay" data-id="element-262" />

      {/* Fullscreen video container */}
      <div ref={videoContainerRef} className={`${isPlaying && !showFinale ? 'block' : 'hidden'} fixed inset-0 z-50 bg-black flex items-center justify-center`} data-id="element-263">
        <video ref={videoRef} className="w-full h-full object-contain bg-black" controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="auto" data-id="element-264">
          <source src="https://cdn.shopify.com/videos/c/o/v/0cc39096110242f5970d52e2bb077a2a.mp4" type="video/mp4" data-id="element-265" />
        </video>
      </div>

      {/* FINALE OVERLAY */}
      <AnimatePresence data-id="element-266">
        {showFinale && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 2
      }} className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center px-6 text-center" data-id="element-267">
            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.8,
          duration: 1.2
        }} className="w-24 h-0.5 bg-[var(--accent)] mb-14 origin-center" data-id="element-268" />

            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 1.2
        }} className="text-2xl md:text-3xl font-serif text-[var(--text-primary)] leading-relaxed mb-3" data-id="element-269">
              El caso terminó.
            </motion.p>

            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.2,
          duration: 1
        }} className="text-xl md:text-2xl font-serif text-[var(--text-secondary)] leading-relaxed italic mb-3" data-id="element-270">
              Pero ahora…
            </motion.p>

            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 2,
          duration: 1
        }} className="text-2xl md:text-3xl font-serif text-[var(--accent)] leading-relaxed font-semibold mb-16" data-id="element-271">
              todo depende de ustedes, detectives.
            </motion.p>

            <AnimatePresence data-id="element-272">
              {showFinText && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 1.5
          }} className="flex flex-col items-center gap-6" data-id="element-273">
                  <div className="w-12 h-px bg-white/20" data-id="element-274" />

                  <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-[0.3em] text-[var(--text-primary)]" data-id="element-275">
                    FIN DE TEMPORADA
                  </h2>

                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.8,
              duration: 1
            }} className="text-[var(--text-secondary)] text-sm font-light tracking-widest uppercase mt-4" data-id="element-276">
                    Próximo caso disponible pronto.
                  </motion.p>
                </motion.div>}
            </AnimatePresence>

            <AnimatePresence data-id="element-277">
              {canDismiss && <motion.button initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} onClick={dismissFinale} className="mt-16 px-10 py-4 border border-[var(--accent)]/50 text-[var(--text-secondary)] uppercase tracking-[0.2em] text-sm transition-all duration-500 hover:border-[var(--accent)] hover:text-[var(--text-primary)] hover:scale-105" data-id="element-278">
                  Cerrar
                </motion.button>}
            </AnimatePresence>

            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 4,
          duration: 1
        }} className="w-16 h-0.5 bg-[var(--accent)]/20 mt-12 origin-center" data-id="element-279" />
          </motion.div>}
      </AnimatePresence>

      {/* Navigation Bar */}
      <nav className="w-full p-6 flex justify-between items-center z-30" data-id="element-280">
        <button onClick={onBack} className="flex items-center text-[var(--text-secondary)] hover:text-white transition-colors text-sm tracking-widest uppercase group" data-id="element-281">
          <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-282" />
          Volver
        </button>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 z-20" data-id="element-283">
        <div className="max-w-4xl w-full mx-auto" data-id="element-284">
          <motion.div variants={itemVariants} className="mb-4" data-id="element-285">
            <span className="text-[var(--text-secondary)] uppercase tracking-[0.2em] text-xs md:text-sm font-medium border-l-2 border-[var(--accent)] pl-4" data-id="element-286">
              Temporada 1 — Caso del Auto Blanco
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[var(--text-primary)] mb-8 leading-tight" data-id="element-287">
            EPISODIO 6 — <br className="md:hidden" data-id="element-288" />
            <span className="text-white" data-id="element-289">LA VERDAD</span>
          </motion.h2>

          <motion.div variants={itemVariants} className="w-24 h-1 bg-[var(--accent)] mb-10" data-id="element-290" />

          <motion.div variants={itemVariants} className="space-y-4 mb-12 max-w-2xl" data-id="element-291">
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-292">
              Las piezas encajan.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-293">
              La verdad sale a la luz.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-294">
              Y ahora entenderán qué ocurrió realmente.
            </p>
          </motion.div>

          {/* Discovery section */}
          <motion.div variants={itemVariants} className="mb-12 max-w-2xl" data-id="element-295">
            <h3 className="text-lg font-serif text-[var(--text-secondary)] uppercase tracking-widest mb-6" data-id="element-296">
              ¿Qué revela este episodio?
            </h3>
            <ul className="space-y-3 text-[var(--text-primary)] font-light" data-id="element-297">
              <li className="flex items-center" data-id="element-298">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-299" />
                La reconstrucción completa de los hechos
              </li>
              <li className="flex items-center" data-id="element-300">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-301" />
                Cómo encajan las pruebas
              </li>
              <li className="flex items-center" data-id="element-302">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-303" />
                El giro final del ADN
              </li>
              <li className="flex items-center" data-id="element-304">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-305" />
                El cierre definitivo del caso
              </li>
            </ul>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12" data-id="element-306">
            <button onClick={handlePlayFullscreen} className="flex items-center justify-center px-8 py-4 bg-[var(--accent)] text-white font-medium tracking-widest uppercase hover:bg-[var(--accent-hover)] transition-all duration-300 shadow-[0_0_30px_rgba(139,26,26,0.3)] hover:shadow-[0_0_50px_rgba(139,26,26,0.5)] hover:scale-105" data-id="element-307">
              <Play className="w-5 h-5 mr-3 fill-current" data-id="element-308" />
              {hasWatched ? 'Volver a Ver' : 'Ver Episodio 6'}
            </button>
          </motion.div>

          {/* Post-video content */}
          <AnimatePresence data-id="element-309">
            {hasWatched && <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="space-y-12 max-w-3xl" data-id="element-310">
                <div className="w-full h-px bg-white/10" data-id="element-311" />

                <motion.div className="bg-[var(--bg-card)] border-l-4 border-[var(--accent)] p-6 md:p-8 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-312">
                  <div className="absolute top-0 right-0 p-4 opacity-5" data-id="element-313">
                    <Scale className="w-24 h-24" data-id="element-314" />
                  </div>
                  <div className="relative z-10" data-id="element-315">
                    <div className="flex items-center mb-4 text-[var(--accent)]" data-id="element-316">
                      <Scale className="w-5 h-5 mr-2" data-id="element-317" />
                      <span className="font-bold tracking-widest uppercase text-sm" data-id="element-318">
                        Final de Temporada
                      </span>
                    </div>
                    <p className="text-[var(--text-primary)] text-lg" data-id="element-319">
                      Este episodio revela la verdad completa del caso.
                    </p>
                  </div>
                </motion.div>

                <motion.div className="flex flex-col items-center pt-4 pb-12 gap-4" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} data-id="element-320">
                  <p className="text-[var(--text-primary)] text-lg font-serif italic" data-id="element-321">
                    Gracias por investigar, detectives.
                  </p>
                  <div className="w-12 h-px bg-[var(--accent)]/30 my-2" data-id="element-322" />
                  <p className="text-[var(--text-secondary)] text-sm uppercase tracking-widest" data-id="element-323">
                    Fin de la Temporada 1
                  </p>
                  <p className="text-[var(--text-secondary)]/60 text-xs tracking-wider mt-2" data-id="element-324">
                    Próximo caso disponible pronto.
                  </p>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}